import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getMsalInstance } from 'authConfig';
import { isPublicBookingPath } from 'helpers/authFlow';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { scopes, type tokenScope } from './scopes';

let reauthRedirectInProgress = false

export const removeAllCookies = () => {
  var cookies = document.cookie.split(';')

  for (var i = 0; i < cookies.length; i++) {
    var cookie = cookies[i]
    var eqPos = cookie.indexOf('=')
    var cookieName = eqPos > -1 ? cookie.substr(0, eqPos) : cookie
    document.cookie =
      cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
  }
}

const getStorage = () => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.localStorage
  } catch {
    return null
  }
}

const getTokenKey = (tokenScope: tokenScope) => `token-scp-${tokenScope}`

const clearReauthRedirectFlag = () => {
  reauthRedirectInProgress = false
}

const triggerReauthRedirect = (reason: string) => {
  if (typeof window === 'undefined') {
    return
  }

  const path = window.location.pathname || '/'
  if (
    path === '/sso' ||
    path.startsWith('/auth') ||
    path.startsWith('/logout') ||
    isPublicBookingPath(path)
  ) {
    return
  }

  if (reauthRedirectInProgress) {
    return
  }
  reauthRedirectInProgress = true

  const currentLocation = `${window.location.pathname}${window.location.search}`
  const target = new URL('/sso', window.location.origin)
  target.searchParams.set('logout_redirect', currentLocation)
  target.searchParams.set('reason', reason)
  window.location.replace(target.toString())
}

const getCachedToken = (tokenScope: tokenScope) => {
  const storage = getStorage()
  if (!storage) {
    return ''
  }

  const key = getTokenKey(tokenScope)
  const storedValue = storage.getItem(key)

  if (!storedValue) {
    return ''
  }

  try {
    const parsed = JSON.parse(storedValue) as {
      token?: string
      expiresAt?: number
    }

    if (parsed.expiresAt && parsed.expiresAt <= Date.now()) {
      storage.removeItem(key)
      return ''
    }

    return parsed.token || ''
  } catch {
    storage.removeItem(key)
    return ''
  }
}

const cacheToken = (tokenScope: tokenScope, token: string, exp?: number) => {
  const storage = getStorage()
  if (!storage) {
    return
  }

  const key = getTokenKey(tokenScope)

  const existing = storage.getItem(key)
  if (existing) {
    try {
      const parsedExisting = JSON.parse(existing) as {
        token?: string
        expiresAt?: number
      }

      if (
        parsedExisting.token &&
        parsedExisting.expiresAt &&
        parsedExisting.expiresAt > Date.now()
      ) {
        return
      }
    } catch {
      // If corrupted, fall through and overwrite
    }
  }

  const payload = JSON.stringify({
    token,
    expiresAt: exp ? exp * 1000 : undefined,
  })

  storage.setItem(key, payload)
}

const clearCachedToken = (tokenScope: tokenScope) => {
  const storage = getStorage()
  if (!storage) {
    return
  }

  storage.removeItem(getTokenKey(tokenScope))
}

const getTokenScope = async (tokenScope: tokenScope) => {
  const cachedToken = getCachedToken(tokenScope)
  if (cachedToken) {
    clearReauthRedirectFlag()
    console.log(
      '[localStorageFunctions] Using cached token for scope:',
      tokenScope,
    )
    return cachedToken
  }

  const msalInstance = await getMsalInstance()
  const activeAccount = msalInstance.getActiveAccount()

  if (!activeAccount) {
    console.warn(
      '[localStorageFunctions] No active account - cannot acquire token silently. User must authenticate first.',
    )
    const error = new InteractionRequiredAuthError(
      'no_account_error',
      'No active account found. User authentication required.',
    )
    clearCachedToken(tokenScope)
    triggerReauthRedirect('no_active_account')
    throw error
  }

  let token = ''

  try {
    console.log(
      '[localStorageFunctions] Attempting acquireTokenSilent for scope:',
      tokenScope,
      'with account:',
      activeAccount.username,
    )
    const silentTokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [scopes[tokenScope]],
      account: activeAccount,
      extraQueryParameters: {
        logonLocation: localStorage.getItem('logonLocation') ?? 'jyrney',
      },
    })

    token = String(silentTokenResponse.accessToken)
    const decodedToken = jwtDecode(token) as JwtPayload
    cacheToken(tokenScope, token, decodedToken?.exp)
    clearReauthRedirectFlag()
    console.log('[localStorageFunctions] acquireTokenSilent succeeded')
  } catch (error) {
    console.error('[localStorageFunctions] acquireTokenSilent failed:', error)
    clearCachedToken(tokenScope)

    // Don't logout on InteractionRequiredAuthError - let DirectSSO handle it
    if (error instanceof InteractionRequiredAuthError) {
      console.log(
        '[localStorageFunctions] InteractionRequiredAuthError - redirecting to DirectSSO flow',
      )
      triggerReauthRedirect('interaction_required')
      throw error
    }

    console.warn(
      '[localStorageFunctions] Non-interactive error, forcing re-auth flow',
    )
    triggerReauthRedirect('token_acquire_failed')

    const message =
      error instanceof Error
        ? error.message
        : 'Silent token acquisition failed.'

    throw new InteractionRequiredAuthError('interaction_required', message)
  }

  return token
}

export function getUserToken(scopeType: tokenScope) {
  return getTokenScope(scopeType)
  // return localStorage.getItem('user')
}
